<template>
  <!--
    InsumoWizard
    -------------------------------------------------------------------------
    Cadastro/edição de insumo em PASSO A PASSO (estilo WhatsApp do sistema):
      1. Tipo      — cards com ícone + explicação. Disponível também na
                     EDIÇÃO (clientes antigos às vezes precisam reclassificar
                     um item, ex.: "Acabamento" que hoje deveria ser "Furo") —
                     ao editar o wizard abre direto no passo 2, mas o passo 1
                     continua na trilha e no botão "← Tipo".
      2. Detalhes  — campos do tipo escolhido (Forma Pronta com template e
                     preview; Furo com símbolo, medidas e unidade cm/mm)
      3. Fotos & salvar — imagens + resumo + salvar/excluir

    Substitui o antigo formulário duplicado (desktop + dialog) do Listar.vue.
    Usa o vuex global (get_insumo) e as actions criar/atualizar_insumo — a
    validação é própria (sem depender de $refs externos).
  -->
  <div class="iw-root fonte" :class="{ 'iw-root--dark': isDark }">
    <v-theme-provider :dark="isDark">
    <div>
    <!-- trilha de passos -->
    <div class="iw-steps">
      <template v-for="(stepInfo, si) in visibleSteps">
        <div
          :key="`iw-step-${stepInfo.value}`"
          class="iw-step"
          :class="{
            'iw-step--active': step === stepInfo.value,
            'iw-step--done': step > stepInfo.value
          }"
          @click="goToStep(stepInfo.value)"
        >
          <span class="iw-step-ball">
            <v-icon v-if="step > stepInfo.value" size="13" color="#fff">mdi-check</v-icon>
            <span v-else>{{ si + 1 }}</span>
          </span>
          <span class="iw-step-label">{{ stepInfo.label }}</span>
        </div>
        <div
          v-if="si < visibleSteps.length - 1"
          :key="`iw-sep-${stepInfo.value}`"
          class="iw-step-sep"
        />
      </template>
      <v-spacer />
      <v-chip
        v-if="get_insumo.type"
        small
        label
        :color="$theme.primary"
        class="white--text iw-type-chip"
      >
        <v-icon left size="14" color="#fff">{{ currentTypeIcon }}</v-icon>
        {{ get_insumo.type }}
      </v-chip>
    </div>

    <v-window v-model="step" class="iw-window">
      <!-- ============ PASSO 1: TIPO ============ -->
      <v-window-item :value="1">
        <div class="iw-section" id="nova-info-geral">
          <span class="iw-section-title">O que você está cadastrando?</span>
          <div class="iw-type-grid">
            <div
              v-for="card in typeCards"
              :key="card.type"
              :id="`Tipo-${card.type.replace(' ', '-')}`"
              class="iw-type-card active-next-step"
              :class="{ 'iw-type-card--selected': get_insumo.type === card.type }"
              @click="selectType(card.type)"
            >
              <v-icon size="26" :color="$theme.primary">{{ card.icon }}</v-icon>
              <span class="iw-type-card-title">{{ card.type }}</span>
              <span class="iw-type-card-desc">{{ card.desc }}</span>
            </div>
          </div>
        </div>
      </v-window-item>

      <!-- ============ PASSO 2: DETALHES ============ -->
      <v-window-item :value="2">
        <div class="iw-section" id="novo-registro-info">
          <div class="iw-fields">
            <v-flex xs12 md5 class="iw-field">
              <v-text-field
                label="Descrição"
                dense
                outlined
                hide-details
                :placeholder="`descrição da ${get_insumo.type || 'peça'}`"
                :color="$theme.primary"
                v-model="get_insumo.nome"
                @input="forceUpdate"
              ></v-text-field>
            </v-flex>
            <v-flex v-if="showPrice" xs12 md3 class="iw-field">
              <v-text-field
                label="Preço"
                dense
                outlined
                hide-details
                type="number"
                :color="$theme.primary"
                placeholder="ex: 12"
                v-model="get_insumo.ultimo_preco_de_compra"
                @input="forceUpdate"
              ></v-text-field>
            </v-flex>
            <v-flex v-if="get_insumo.type === 'Assinatura'" xs12 md3 class="iw-field">
              <v-select
                label="Período"
                dense
                outlined
                hide-details
                :items="['diaria', 'mensal', 'semestral', 'anual']"
                :color="$theme.primary"
                v-model="get_insumo.categoria"
                @input="forceUpdate"
              ></v-select>
            </v-flex>
            <v-flex
              v-if="['Placa', 'Material'].includes(get_insumo.type)"
              xs12
              md3
              class="iw-field"
            >
              <v-text-field
                label="Estoque mínimo"
                dense
                outlined
                hide-details
                type="number"
                :color="$theme.primary"
                v-model="get_insumo.estoque_minimo"
                @input="forceUpdate"
              ></v-text-field>
            </v-flex>
            <v-flex v-if="get_insumo.type === 'Placa'" xs12 md2 class="iw-field">
              <v-text-field
                label="Espessura"
                dense
                outlined
                hide-details
                type="number"
                suffix="mm"
                :color="$theme.primary"
                :value="get_insumo.espessura_mm || get_insumo.espessuraMm || 10"
                @input="setPlateThickness"
              ></v-text-field>
            </v-flex>
            <v-flex v-if="get_insumo.type === 'Material'" xs12 md3 class="iw-field">
              <v-select
                label="Unidade"
                dense
                outlined
                hide-details
                :items="unidades_de_medida_base"
                item-text="nome"
                return-object
                :color="$theme.primary"
                v-model="get_insumo.unidade_estoque_minimo"
                @input="forceUpdate"
              ></v-select>
            </v-flex>
            <!-- PEÇA SECUNDÁRIA: como ela se comporta em relação à principal
                 (define o desenho na perspectiva/impressão: saia cai curvada
                 para baixo, frontão sobe, etc.) -->
            <v-flex v-if="get_insumo.type === 'Peça Secundária'" xs12 md4 class="iw-field">
              <v-select
                label="Relação com a peça principal"
                dense
                outlined
                hide-details
                :items="relacaoOptions"
                item-text="label"
                item-value="value"
                :color="$theme.primary"
                :value="get_insumo.relacao_principal || get_insumo.relacaoPrincipal || 'neutra'"
                @change="setRelacaoPrincipal"
              ></v-select>
            </v-flex>
            <v-flex xs12 md2 class="iw-field iw-switch">
              <v-switch
                label="Disponível"
                dense
                hide-details
                class="ma-0 pa-0"
                :color="$theme.primary"
                v-model="get_insumo.disponivel"
              ></v-switch>
            </v-flex>
          </div>

          <div v-if="get_insumo.type === 'Acabamento'" class="iw-card">
            <span class="iw-card-title">Onde este acabamento é aplicado?</span>
            <div class="iw-fields mt-2">
              <v-flex xs12 md6 class="iw-field">
                <v-select
                  dense
                  outlined
                  hide-details
                  label="Alvo do acabamento"
                  :items="finishTargetOptions"
                  item-text="label"
                  item-value="value"
                  :color="$theme.primary"
                  :value="get_insumo.finish_target || 'borda'"
                  @change="setFinishTarget"
                ></v-select>
              </v-flex>
            </div>
            <span class="iw-muted">
              Borda aparece ao selecionar uma aresta; peça aparece ao selecionar a superfície.
            </span>
          </div>

          <!-- ===== FORMA PRONTA: template + presets + preview ===== -->
          <div v-if="get_insumo.type === 'Forma Pronta'" class="iw-card">
            <span class="iw-card-title">Template da Forma Pronta</span>
            <div class="iw-inline-actions">
              <v-btn x-small outlined :color="$theme.primary" class="text-capitalize" @click="applyFormaPreset('rect')">
                <v-icon left size="14">mdi-rectangle-outline</v-icon> Bancada reta
              </v-btn>
              <v-btn x-small outlined :color="$theme.primary" class="text-capitalize" @click="applyFormaPreset('L')">
                <v-icon left size="14">mdi-format-letter-case</v-icon> Bancada em L
              </v-btn>
              <v-btn x-small outlined :color="$theme.primary" class="text-capitalize" @click="applyFormaPreset('U')">
                <v-icon left size="14">mdi-alpha-u-box-outline</v-icon> Bancada em U
              </v-btn>
            </div>
            <div v-if="formaPreviewPieces.length" class="iw-preview">
              <DrawingCanvas
                :pieces="formaPreviewPieces"
                :width="640"
                :height="240"
                :show-grid="false"
                :show-measurements="true"
                :technical-style="true"
                :readonly="true"
              />
            </div>
            <div class="iw-fields mt-2">
              <v-flex xs6 md1 class="iw-field">
                <v-text-field
                  dense outlined hide-details label="Qtd"
                  v-model="forma_pronta_form.quantidade"
                ></v-text-field>
              </v-flex>
              <v-flex xs6 md4 class="iw-field">
                <v-autocomplete
                  dense outlined hide-details label="Tipo de peça"
                  :items="insumosParaTemplate"
                  item-text="nome"
                  return-object
                  v-model="forma_pronta_form.insumo"
                ></v-autocomplete>
              </v-flex>
              <v-flex
                v-if="forma_pronta_form.insumo && forma_pronta_form.insumo.type !== 'Acabamento'"
                xs6 md2 class="iw-field"
              >
                <v-text-field
                  dense outlined hide-details label="Comprimento"
                  v-model="forma_pronta_form.comprimento"
                ></v-text-field>
              </v-flex>
              <v-flex
                v-if="forma_pronta_form.insumo && forma_pronta_form.insumo.type !== 'Acabamento'"
                xs6 md2 class="iw-field"
              >
                <v-text-field
                  dense outlined hide-details label="Largura"
                  v-model="forma_pronta_form.largura"
                ></v-text-field>
              </v-flex>
              <v-flex xs12 md2 class="iw-field">
                <v-btn
                  block depressed small
                  :color="$theme.primary"
                  class="white--text text-capitalize"
                  @click="addItemTemplate"
                >Adicionar</v-btn>
              </v-flex>
            </div>
            <v-list dense color="transparent" class="pa-0">
              <v-list-item
                v-for="(itemForma, i) in (get_insumo.ficha_tecnica || [])"
                :key="`iw-forma-${i}`"
                class="px-0"
              >
                <v-list-item-content>
                  <v-list-item-title class="fonte">
                    {{ itemForma.quantity }}x {{ itemForma.descricao }}
                  </v-list-item-title>
                  <v-list-item-subtitle>
                    {{ itemForma.type }}
                    <span v-if="itemForma.type !== 'Acabamento'">
                      - {{ itemForma.comprimento }} x {{ itemForma.largura }}
                    </span>
                  </v-list-item-subtitle>
                </v-list-item-content>
                <v-list-item-action>
                  <v-btn icon x-small @click="removeItemTemplate(i)">
                    <v-icon color="red">mdi-delete</v-icon>
                  </v-btn>
                </v-list-item-action>
              </v-list-item>
            </v-list>
          </div>

          <!-- ===== FURO: símbolo + medidas com unidade cm/mm + preview ===== -->
          <div v-if="get_insumo.type === 'Furo'" class="iw-card">
            <span class="iw-card-title">
              Configuração do Furo
              <span class="iw-muted">— aparece no desenho técnico em "Inserir recorte"</span>
            </span>
            <div class="iw-fields mt-2">
              <v-flex xs12 md4 class="iw-field">
                <v-select
                  dense outlined hide-details
                  label="Imagem padrão (símbolo)"
                  :items="furoSymbolOptions"
                  item-text="label"
                  item-value="kind"
                  :color="$theme.primary"
                  :value="furoConfig.kind"
                  @change="setFuroConfig('kind', $event)"
                ></v-select>
              </v-flex>
              <v-flex xs12 md2 class="iw-field">
                <v-btn-toggle
                  v-model="furoUnit"
                  mandatory
                  dense
                >
                  <v-btn value="cm" x-small class="text-lowercase">cm</v-btn>
                  <v-btn value="mm" x-small class="text-lowercase">mm</v-btn>
                </v-btn-toggle>
              </v-flex>
              <template v-if="furoIsCircular">
                <v-flex xs12 md3 class="iw-field">
                  <v-text-field
                    dense outlined hide-details type="number"
                    :label="`Diâmetro (${furoConfig.unidade})`"
                    :value="furoConfig.diametro"
                    @change="setFuroConfig('diametro', $event)"
                  ></v-text-field>
                </v-flex>
              </template>
              <template v-else>
                <v-flex xs6 md2 class="iw-field">
                  <v-text-field
                    dense outlined hide-details type="number"
                    :label="`Largura (${furoConfig.unidade})`"
                    :value="furoConfig.largura"
                    @change="setFuroConfig('largura', $event)"
                  ></v-text-field>
                </v-flex>
                <v-flex xs6 md2 class="iw-field">
                  <v-text-field
                    dense outlined hide-details type="number"
                    :label="`Altura (${furoConfig.unidade})`"
                    :value="furoConfig.altura"
                    @change="setFuroConfig('altura', $event)"
                  ></v-text-field>
                </v-flex>
              </template>
              <v-flex xs12 md2 class="iw-field">
                <svg
                  width="86"
                  height="64"
                  viewBox="0 0 86 64"
                  class="iw-furo-preview"
                >
                  <template v-for="(prim, pi) in furoPreviewPrimitives">
                    <rect
                      v-if="prim.type === 'rect'"
                      :key="`iw-furo-rect-${pi}`"
                      :x="prim.x" :y="prim.y" :width="prim.width" :height="prim.height"
                      :rx="prim.rx || 0"
                      fill="#fff" stroke="#173b33" stroke-width="1.1"
                      :stroke-dasharray="prim.dash || ''"
                    />
                    <circle
                      v-else-if="prim.type === 'circle'"
                      :key="`iw-furo-circle-${pi}`"
                      :cx="prim.cx" :cy="prim.cy" :r="prim.r"
                      fill="none" stroke="#173b33" stroke-width="1"
                    />
                  </template>
                </svg>
              </v-flex>
            </div>
          </div>

          <!-- explicação curta do tipo escolhido -->
          <div v-if="currentTypeDesc" class="iw-hint">
            <v-icon size="15" color="#5a6660">mdi-information-outline</v-icon>
            <span>{{ currentTypeDesc }}</span>
          </div>

          <div class="iw-actions">
            <!-- reclassificar (ex.: itens antigos de "Acabamento" que hoje
                 são "Furo") — liberado também na edição -->
            <v-btn text small class="text-capitalize" @click="step = 1">
              <v-icon left size="16">mdi-chevron-left</v-icon> Tipo
            </v-btn>
            <v-spacer />
            <v-btn
              depressed
              :color="$theme.primary"
              class="white--text text-capitalize"
              @click="goToImages"
            >
              Continuar
              <v-icon right size="16">mdi-chevron-right</v-icon>
            </v-btn>
          </div>
        </div>
      </v-window-item>

      <!-- ============ PASSO 3: FOTOS & SALVAR ============ -->
      <v-window-item :value="3">
        <div class="iw-section">
          <span class="iw-section-title">
            Foto do item
            <span v-if="get_insumo.type === 'Acabamento'" class="iw-muted">
              — use uma imagem quadrada mostrando como o acabamento é; ela
              aparece no alvo escolhido no desenho técnico
            </span>
          </span>
          <div class="expande-horizontal centraliza">
            <ModalSendArchive @input="save" />
          </div>

          <!-- resumo -->
          <div class="iw-card iw-summary">
            <div class="iw-summary-row">
              <span class="iw-muted">Item</span>
              <strong>{{ get_insumo.nome || "—" }}</strong>
            </div>
            <div class="iw-summary-row">
              <span class="iw-muted">Tipo</span>
              <strong>{{ get_insumo.type }}</strong>
            </div>
            <div v-if="showPrice" class="iw-summary-row">
              <span class="iw-muted">Preço</span>
              <strong>{{ $helper.formataSaldo(get_insumo.ultimo_preco_de_compra || 0) }}</strong>
            </div>
            <div v-if="get_insumo.type === 'Furo'" class="iw-summary-row">
              <span class="iw-muted">Medidas</span>
              <strong>{{ furoResumo }}</strong>
            </div>
            <div v-if="get_insumo.type === 'Acabamento'" class="iw-summary-row">
              <span class="iw-muted">Aplicação</span>
              <strong>{{ finishTargetLabel }}</strong>
            </div>
            <div v-if="get_insumo.type === 'Forma Pronta'" class="iw-summary-row">
              <span class="iw-muted">Template</span>
              <strong>{{ (get_insumo.ficha_tecnica || []).length }} item(ns)</strong>
            </div>
          </div>

          <div class="iw-actions">
            <v-btn text small class="text-capitalize" @click="step = 2">
              <v-icon left size="16">mdi-chevron-left</v-icon> Detalhes
            </v-btn>
            <v-btn
              v-if="isEditing"
              text
              small
              color="red"
              class="text-capitalize"
              @click="$emit('delete')"
            >
              <v-icon left size="16">mdi-delete</v-icon> Excluir
            </v-btn>
            <v-spacer />
            <v-btn
              id="salvar-btn"
              depressed
              :color="$theme.primary"
              class="white--text text-capitalize"
              :loading="saving"
              @click="save"
            >
              <v-icon left size="16">mdi-check</v-icon>
              {{ isEditing ? "Salvar alterações" : "Salvar item" }}
            </v-btn>
          </div>
        </div>
      </v-window-item>
    </v-window>
    </div>
    </v-theme-provider>
  </div>
</template>

<script>
import { mapActions, mapGetters } from "vuex";
import ModalSendArchive from "../../shared/components/ModalSendArchive.vue";
import DrawingCanvas from "@/components/stone-technical-drawing/DrawingCanvas.vue";
import { normalizeFichaTecnica } from "@/components/stone-technical-drawing/engine/drawing/normalizeFichaTecnica";
import {
  CUTOUT_TYPES,
  isCircularKind
} from "@/components/stone-technical-drawing/engine/drawing/cutoutUtils";
import { getCutoutSymbol } from "@/components/stone-technical-drawing/engine/drawing/symbolLibrary";

const TYPE_CARDS = [
  {
    type: "Placa",
    icon: "mdi-view-grid-outline",
    desc: "A chapa de granito/mármore. Aparece no caixa como base do orçamento."
  },
  {
    type: "Peça Principal",
    icon: "mdi-countertop-outline",
    desc: "A peça central: balcão, bancada, soleira..."
  },
  {
    type: "Peça Secundária",
    icon: "mdi-view-agenda-outline",
    desc: "Liga-se à principal: saia, rodapé, frontão..."
  },
  {
    type: "Acabamento",
    icon: "mdi-texture-box",
    desc: "Aplicado na borda da peça. Cadastre com imagem quadrada."
  },
  {
    type: "Furo",
    icon: "mdi-record-circle-outline",
    desc: "Recorte padrão do desenho: cuba, cooktop, torneira..."
  },
  {
    type: "Forma Pronta",
    icon: "mdi-shape-outline",
    desc: "Template de peça completa para orçamentos rápidos."
  },
  {
    type: "Material",
    icon: "mdi-package-variant-closed",
    desc: "Consumíveis com estoque: lixa, massa, cola..."
  },
  {
    type: "Assinatura",
    icon: "mdi-autorenew",
    desc: "Serviço recorrente com período."
  }
];

const PRICE_TYPES = ["Forma Pronta", "Placa", "Material", "Acabamento", "Assinatura", "Furo"];

export default {
  name: "InsumoWizard",
  components: { ModalSendArchive, DrawingCanvas },
  data() {
    return {
      step: 1,
      saving: false,
      forma_pronta_form: { quantidade: 1, insumo: null, comprimento: "", largura: "" },
      // como a peça secundária se liga à principal (usado pela perspectiva)
      relacaoOptions: [
        { value: "neutra", label: "Neutra (mesmo plano)" },
        { value: "curva_baixo", label: "Curvada para baixo (saia)" },
        { value: "curva_cima", label: "Curvada para cima (frontão)" },
        { value: "sobreposta_acima", label: "Sobreposta acima" },
        { value: "sobreposta_abaixo", label: "Sobreposta abaixo" }
      ],
      finishTargetOptions: [
        { value: "borda", label: "Na borda (aresta)" },
        { value: "peca", label: "Na superfície da peça" }
      ],
      unidades_de_medida_base: [
        { nome: "KG", valor: "KG", divisor: 1000 },
        { nome: "GRAMA", valor: "GR", divisor: 1 },
        { nome: "LITRO", valor: "LT", divisor: 1000 },
        { nome: "MILILITRO", valor: "ML", divisor: 1 },
        { nome: "UNIDADE", valor: "UN", divisor: 1 },
        { nome: "Metro Quadrado", valor: "M2", divisor: 100 }
      ]
    };
  },
  computed: {
    ...mapGetters(["get_insumo", "get_insumos"]),
    // condição oficial de tema escuro do sistema
    isDark() {
      return localStorage.dark === "true";
    },
    isEditing() {
      return !!this.get_insumo._id;
    },
    visibleSteps() {
      // "Tipo" fica visível SEMPRE (também editando): clientes antigos
      // precisam reclassificar itens (ex.: Acabamento → Furo, tipo novo
      // que antes não existia). Editar só abre direto no passo 2 por
      // padrão — o passo 1 continua acessível pela trilha/botão "← Tipo".
      return [
        { value: 1, label: "Tipo" },
        { value: 2, label: "Detalhes" },
        { value: 3, label: "Foto & salvar" }
      ];
    },
    typeCards() {
      return TYPE_CARDS;
    },
    currentTypeIcon() {
      const card = TYPE_CARDS.find(c => c.type === this.get_insumo.type);
      return card ? card.icon : "mdi-cog-outline";
    },
    currentTypeDesc() {
      const card = TYPE_CARDS.find(c => c.type === this.get_insumo.type);
      return card ? card.desc : "";
    },
    showPrice() {
      return PRICE_TYPES.includes(this.get_insumo.type);
    },
    insumosParaTemplate() {
      return (this.get_insumos.docs || []).filter(insumo =>
        ["Peça Principal", "Peça Secundária", "Acabamento"].includes(insumo.type)
      );
    },
    formaPreviewPieces() {
      return normalizeFichaTecnica(this.get_insumo.ficha_tecnica || []);
    },
    furoSymbolOptions() {
      return CUTOUT_TYPES.map(c => ({ kind: c.kind, label: c.label }));
    },
    // config do Furo com defaults + compat com o schema antigo (*_cm)
    furoConfig() {
      const config = this.get_insumo.furo_config || {};
      return {
        kind: config.kind || "hole",
        unidade: config.unidade || "cm",
        largura: config.largura != null ? config.largura : config.largura_cm || 50,
        altura: config.altura != null ? config.altura : config.altura_cm || 40,
        diametro: config.diametro != null ? config.diametro : config.diametro_cm || 10
      };
    },
    furoIsCircular() {
      return isCircularKind(this.furoConfig.kind);
    },
    furoUnit: {
      get() {
        return this.furoConfig.unidade;
      },
      set(value) {
        if (value) this.setFuroConfig("unidade", value);
      }
    },
    furoPreviewPrimitives() {
      const circular = this.furoIsCircular;
      return getCutoutSymbol(this.furoConfig.kind, {
        cx: 43,
        cy: 32,
        w: circular ? 40 : 56,
        h: 40
      });
    },
    furoResumo() {
      const c = this.furoConfig;
      return this.furoIsCircular
        ? `Ø ${c.diametro}${c.unidade}`
        : `${c.largura} × ${c.altura}${c.unidade}`;
    },
    finishTargetLabel() {
      const target = this.get_insumo.finish_target || "borda";
      const option = this.finishTargetOptions.find(item => item.value === target);
      return option ? option.label : "—";
    }
  },
  watch: {
    // trocou o insumo em edição (ou abriu um novo): reposiciona o wizard
    "get_insumo._id"() {
      this.resetWizard();
    },
    "get_insumo.new"() {
      this.resetWizard();
    }
  },
  mounted() {
    this.resetWizard();
  },
  methods: {
    ...mapActions(["criar_insumo", "atualizar_insumo", "createGlobalMessage"]),
    forceUpdate() {
      this.$forceUpdate();
    },
    resetWizard() {
      // editar abre direto em Detalhes (a maioria só ajusta campos); o
      // passo Tipo continua acessível pela trilha/botão "← Tipo" para quem
      // precisa reclassificar o item.
      this.step = this.isEditing ? 2 : 1;
      this.forma_pronta_form = { quantidade: 1, insumo: null, comprimento: "", largura: "" };
    },
    goToStep(target) {
      // só permite voltar/avançar por passos já visitáveis
      if (target < this.step) this.step = target;
    },
    // ============ passo 1: tipo ============
    selectType(productType) {
      // reabrir o passo Tipo na EDIÇÃO e clicar no tipo que já está
      // selecionado só avança pra Detalhes — não reseta unidade/disponível
      // (evita apagar um "Material" desativado ou com unidade customizada
      // só por revisitar a trilha)
      if (productType === this.get_insumo.type) {
        this.step = 2;
        this.$emit("type-selected", productType);
        return;
      }
      let unidade_de_medida = { nome: "Metro Quadrado", valor: "M2", divisor: 100 };
      if (["Acabamento", "Furo", "Assinatura"].includes(productType)) {
        unidade_de_medida = { nome: "UNIDADE", valor: "UN", divisor: 1 };
      }
      this.$store.commit("set_insumo", {
        ...this.get_insumo,
        type: productType,
        unidade_estoque_minimo: unidade_de_medida,
        disponivel: true,
        ...(productType === "Placa"
          ? {
              espessura_mm:
                this.get_insumo.espessura_mm || this.get_insumo.espessuraMm || 10,
              espessuraMm:
                this.get_insumo.espessuraMm || this.get_insumo.espessura_mm || 10
            }
          : {}),
        ...(productType === "Peça Secundária"
          ? {
              relacao_principal:
                this.get_insumo.relacao_principal ||
                this.get_insumo.relacaoPrincipal ||
                "neutra",
              relacaoPrincipal:
                this.get_insumo.relacaoPrincipal ||
                this.get_insumo.relacao_principal ||
                "neutra"
            }
          : {}),
        ...(productType === "Acabamento"
          ? { finish_target: this.get_insumo.finish_target || "borda" }
          : {})
      });
      if (productType === "Forma Pronta" && !this.get_insumo.ficha_tecnica) {
        this.$set(this.get_insumo, "ficha_tecnica", []);
      }
      if (productType === "Furo" && !this.get_insumo.furo_config) {
        this.$set(this.get_insumo, "furo_config", {
          kind: "hole",
          unidade: "cm",
          largura: 50,
          altura: 40,
          diametro: 10
        });
      }
      this.step = 2;
      this.$emit("type-selected", productType);
    },
    // ============ passo 2: detalhes ============
    goToImages() {
      if (!this.validateDetails()) return;
      this.step = 3;
    },
    validateDetails() {
      const info = message => {
        this.createGlobalMessage({ type: "info", message, timeout: 5000 });
        return false;
      };
      if (!this.get_insumo.type) return info("Escolha o tipo do item.");
      if (!`${this.get_insumo.nome || ""}`.trim()) return info("Informe a descrição do item.");
      if (this.showPrice && !this.get_insumo.ultimo_preco_de_compra) {
        return info("Informe o preço do item.");
      }
      if (
        this.get_insumo.type === "Forma Pronta" &&
        (!this.get_insumo.ficha_tecnica || !this.get_insumo.ficha_tecnica.length)
      ) {
        return info("Adicione pelo menos um item no template da Forma Pronta.");
      }
      if (
        this.get_insumo.type === "Acabamento" &&
        !["peca", "borda"].includes(this.get_insumo.finish_target || "borda")
      ) {
        return info("Escolha onde o acabamento será aplicado.");
      }
      if (this.get_insumo.type === "Furo") {
        const c = this.furoConfig;
        const ok = this.furoIsCircular
          ? parseFloat(c.diametro) > 0
          : parseFloat(c.largura) > 0 && parseFloat(c.altura) > 0;
        if (!ok) return info("Informe as medidas do furo.");
      }
      return true;
    },
    setRelacaoPrincipal(value) {
      this.$set(this.get_insumo, "relacao_principal", value);
      this.$set(this.get_insumo, "relacaoPrincipal", value);
      this.$forceUpdate();
    },
    setFinishTarget(value) {
      this.$set(this.get_insumo, "finish_target", value);
      this.$forceUpdate();
    },
    setPlateThickness(value) {
      const parsed = parseFloat(`${value}`.replace(",", ".")) || 10;
      this.$set(this.get_insumo, "espessura_mm", parsed);
      this.$set(this.get_insumo, "espessuraMm", parsed);
      this.$forceUpdate();
    },
    setFuroConfig(field, value) {
      // grava sempre o schema novo completo (kind/unidade/medidas) — o
      // legado *_cm continua sendo lido pelo cutoutFromInsumo
      const config = { ...this.furoConfig, [field]: value };
      this.$set(this.get_insumo, "furo_config", config);
      this.$forceUpdate();
    },
    // ============ Forma Pronta ============
    applyFormaPreset(kind) {
      const principal = (this.get_insumos.docs || []).find(
        i => i.type === "Peça Principal"
      );
      const base = {
        descricao: principal ? principal.nome : "Bancada",
        type: "Peça Principal",
        quantity: 1,
        comprimento: kind === "rect" ? 2.0 : kind === "L" ? 2.4 : 3.0,
        largura: kind === "rect" ? 0.6 : 1.5,
        position: { x: 50, y: 45, z: 0 },
        edgeFinishType: "polida",
        edgeFinishes: ["front"],
        cutouts: [],
        edgeLabels: {}
      };
      if (kind === "L") {
        base.shape = { kind: "L", params: { cutW: 1.6, cutH: 0.9, corner: "tr" } };
      } else if (kind === "U") {
        base.shape = { kind: "U", params: { cutW: 1.6, cutH: 0.9 } };
      }
      this.$set(this.get_insumo, "ficha_tecnica", [base]);
      this.$forceUpdate();
    },
    addItemTemplate() {
      const current = this.forma_pronta_form;
      if (!current.insumo) return;
      if (!this.get_insumo.ficha_tecnica) {
        this.$set(this.get_insumo, "ficha_tecnica", []);
      }
      const quantity = parseInt(current.quantidade) || 1;
      const isAcabamento = current.insumo.type === "Acabamento";
      this.get_insumo.ficha_tecnica.push({
        descricao: current.insumo.nome,
        type: current.insumo.type,
        quantity,
        comprimento: isAcabamento ? 0 : parseFloat(`${current.comprimento || 0}`.replace(",", ".")),
        largura: isAcabamento ? 0 : parseFloat(`${current.largura || 0}`.replace(",", ".")),
        position: { x: 50, y: 50, z: 0 }
      });
      this.forma_pronta_form = { quantidade: 1, insumo: null, comprimento: "", largura: "" };
      this.$forceUpdate();
    },
    removeItemTemplate(index) {
      this.$set(
        this.get_insumo,
        "ficha_tecnica",
        (this.get_insumo.ficha_tecnica || []).filter((item, i) => i !== index)
      );
      this.$forceUpdate();
    },
    // ============ passo 3: salvar ============
    async save() {
      if (!this.validateDetails()) {
        this.step = 2;
        return;
      }
      this.saving = true;
      try {
        if (this.isEditing) {
          await this.atualizar_insumo();
        } else {
          await this.criar_insumo();
        }
        this.$emit("saved");
      } finally {
        this.saving = false;
      }
    }
  }
};
</script>

<style scoped>
.iw-root {
  width: 100%;
}
/* trilha de passos (estilo WhatsApp: pills verdes, cantos arredondados) */
.iw-steps {
  align-items: center;
  background: var(--v-surface-base, #ffffff);
  border-bottom: 1px solid rgba(120, 106, 78, 0.14);
  display: flex;
  gap: 6px;
  padding: 10px 14px;
}
.iw-step {
  align-items: center;
  border-radius: 999px;
  cursor: pointer;
  display: inline-flex;
  gap: 6px;
  opacity: 0.55;
  padding: 3px 10px 3px 4px;
  transition: 0.18s ease;
}
.iw-step--active {
  background: rgba(18, 140, 126, 0.1);
  opacity: 1;
}
.iw-step--done {
  opacity: 0.9;
}
.iw-step-ball {
  align-items: center;
  background: #128c7e;
  border-radius: 50%;
  color: #fff;
  display: inline-flex;
  font-size: 11px;
  font-weight: 700;
  height: 20px;
  justify-content: center;
  width: 20px;
}
.iw-step-label {
  font-size: 12px;
  font-weight: 600;
}
.iw-step-sep {
  background: rgba(120, 106, 78, 0.25);
  height: 1px;
  width: 18px;
}
.iw-type-chip {
  border-radius: 8px !important;
}
.iw-window {
  padding: 4px 0 12px;
}
.iw-section {
  display: flex;
  flex-direction: column;
  gap: 10px;
  padding: 12px 14px;
}
.iw-section-title {
  font-size: 14px;
  font-weight: 700;
}
/* grid de tipos */
.iw-type-grid {
  display: grid;
  gap: 10px;
  grid-template-columns: repeat(auto-fill, minmax(190px, 1fr));
}
.iw-type-card {
  background: var(--v-surface-base, #ffffff);
  border: 1px solid rgba(120, 106, 78, 0.16);
  border-radius: 14px;
  cursor: pointer;
  display: flex;
  flex-direction: column;
  gap: 4px;
  min-height: 108px;
  padding: 12px;
  transition: 0.18s ease;
}
.iw-type-card:hover {
  border-color: #128c7e;
  box-shadow: 0 4px 14px rgba(18, 140, 126, 0.14);
  transform: translateY(-1px);
}
.iw-type-card--selected {
  border-color: #128c7e;
  box-shadow: inset 0 0 0 1px #128c7e;
}
.iw-type-card-title {
  font-size: 13px;
  font-weight: 700;
}
.iw-type-card-desc {
  color: #74827c;
  font-size: 11px;
  line-height: 1.35;
}
/* campos */
.iw-fields {
  display: flex;
  flex-wrap: wrap;
  gap: 8px 0;
}
.iw-field {
  padding-right: 8px;
}
.iw-switch {
  align-items: center;
  display: flex;
}
/* cards internos (forma pronta / furo) */
.iw-card {
  background: var(--v-surface-base, #ffffff);
  border: 1px solid rgba(120, 106, 78, 0.16);
  border-radius: 14px;
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: 12px;
}
.iw-card-title {
  font-size: 13px;
  font-weight: 700;
}
.iw-muted {
  color: #74827c;
  font-size: 11px;
  font-weight: 400;
}
.iw-inline-actions {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
}
.iw-preview {
  border: 1px solid rgba(7, 94, 84, 0.16);
  border-radius: 10px;
  overflow: hidden;
}
.iw-furo-preview {
  background: #fff;
  border: 1px solid rgba(7, 94, 84, 0.16);
  border-radius: 8px;
}
.iw-hint {
  align-items: center;
  background: rgba(18, 140, 126, 0.06);
  border-radius: 10px;
  color: #5a6660;
  display: flex;
  font-size: 12px;
  gap: 8px;
  padding: 8px 10px;
}
.iw-actions {
  align-items: center;
  display: flex;
  gap: 8px;
  margin-top: 4px;
}
.iw-summary {
  gap: 4px;
}
.iw-summary-row {
  display: flex;
  font-size: 12.5px;
  justify-content: space-between;
}

/* ===== TEMA ESCURO (localStorage.dark === "true") =====
   Tudo que tinha fundo/texto claro fixo ganha a variante escura. */
.iw-root--dark {
  color: #e9edef;
}
.iw-root--dark .iw-steps {
  background: #111b21;
  border-bottom-color: #26343c;
}
.iw-root--dark .iw-step--active {
  background: rgba(37, 211, 102, 0.12);
}
.iw-root--dark .iw-step-ball {
  background: #25d366;
  color: #0b141a;
}
.iw-root--dark .iw-step-label,
.iw-root--dark .iw-section-title,
.iw-root--dark .iw-type-card-title,
.iw-root--dark .iw-card-title,
.iw-root--dark .iw-summary-row strong {
  color: #e9edef;
}
.iw-root--dark .iw-type-card,
.iw-root--dark .iw-card {
  background: #182229;
  border-color: #26343c;
}
.iw-root--dark .iw-type-card:hover {
  border-color: #25d366;
  box-shadow: 0 4px 14px rgba(37, 211, 102, 0.14);
}
.iw-root--dark .iw-type-card--selected {
  border-color: #25d366;
  box-shadow: inset 0 0 0 1px #25d366;
}
.iw-root--dark .iw-type-card-desc,
.iw-root--dark .iw-muted,
.iw-root--dark .iw-step-label {
  color: #aebac1;
}
.iw-root--dark .iw-hint {
  background: rgba(37, 211, 102, 0.08);
  color: #aebac1;
}
.iw-root--dark .iw-step-sep {
  background: #26343c;
}
.iw-root--dark .iw-preview,
.iw-root--dark .iw-furo-preview {
  border-color: #26343c;
}
</style>
